'use client';

import { ProductListItem } from './product-list-item';
import { ViewMoreButton } from './view-more-button';
import { useSearchResultsQuery } from '~/hooks/react-query/use-search-results-query';
import { type GetSearchResponse } from '~/trpc/routers/search-router';
import { Fragment } from 'react';
import { SliTrackingClient } from '../tracking/sli-tracking-client';
import { GtmEventComponent } from '../head/gtm-event-component';
import { mapProductToGtmItem } from '~/utils/gtm/gtm-helpers';

type ViewMoreSearchProps = {
  deviceLimit?: number;
  fecomMediaBaseUrl: string;
  phrase: string;
  facets?: Record<string, string[]>;
  sort?: string;
  defaultProductsData: GetSearchResponse;
};

export function ViewMoreSearch({
  phrase,
  defaultProductsData,
  facets,
  sort,
  fecomMediaBaseUrl,
  deviceLimit = 20,
}: ViewMoreSearchProps) {
  const products = useSearchResultsQuery(
    {
      phrase,
      facets,
      sort,
      pageSize: deviceLimit,
    },
    defaultProductsData,
  );

  const totalCount = defaultProductsData?.result.totalCount ?? 0;

  const shownProducts =
    products.data?.pages.flatMap((page) => page?.result.products ?? [])
      .length ?? 0;

  const progress = totalCount ? (shownProducts / totalCount) * 100 : 0;

  return (
    <>
      {products.data?.pages.map((page, index) => (
        <Fragment key={index}>
          <GtmEventComponent
            event={{
              event: 'view_item_list',
              ecommerce: {
                currency: 'GBP',
                item_list_name: 'Search',
                item_list_id: 'search',
                items:
                  page?.result.products.map((product) =>
                    mapProductToGtmItem({
                      product,
                      quantity: product.minOrderQuantity,
                    }),
                  ) ?? [],
              },
            }}
          />
          {page?.result.products?.map((product) => (
            <ProductListItem
              key={product.catalogueRef}
              product={product}
              fecomMediaBaseUrl={fecomMediaBaseUrl}
              gtmListId={`search`}
              gtmListName={'Search'}
            />
          ))}
          {page?.sliPageId && page.sliBeaconUserId ? (
            <SliTrackingClient
              pageId={page.sliPageId}
              sliBeaconUserId={page.sliBeaconUserId}
              trackingCode={page?.result.sliTrackingCode}
            />
          ) : null}
        </Fragment>
      ))}

      <ViewMoreButton
        disabled={products.isFetchingNextPage}
        hasNextPage={products.hasNextPage}
        totalCount={totalCount}
        shownProducts={shownProducts}
        progress={progress}
        onViewMoreClick={() => {
          void products.fetchNextPage();
        }}
      />
    </>
  );
}
